'use client'

import {
  Fragment,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'

import { faCircleCheck, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { createPortal } from 'react-dom'
import {
  FormattedMessage,
  FormattedNumber,
  type IntlShape,
  useIntl,
} from 'react-intl'

import {
  Countdown,
  CurrencyStringDollars,
  Tooltip,
} from '@b-stock/fe-core/bstock-react'
import { ListingDtoPricingStrategyEnum } from '@b-stock/listing-api-client'

import {
  getDisplayBuyingFormats,
  getPrimaryBuyingFormat,
} from '@/buyer-portal/helpers/auctions/buyingFormat'
import Image from '@/components/Image'
import {
  toListingsBidOn,
  useCompareBadgeProps,
} from '@/components/listings/compare/compareAnalytics'
import { CompareCurrencyBanner } from '@/components/listings/compare/CompareCurrencyBanner'
import { useCompareSelection } from '@/components/listings/compare/CompareSelectionProvider'
import { COMPARE_CARD_STACKED_ATTR } from '@/components/listings/compare/constants'
import BuyingFormat from '@/components/listings/listingCard/BaseListingCard/ListingInfo/BuyingFormat'
import { transformCondition } from '@/components/listings/listingCard/BaseListingCard/ListingInfo/InventoryTypeAndCondition'
import StorefrontLogo from '@/components/listings/listingCard/BaseListingCard/ListingInfo/StorefrontLogo'
import { SkeletonTextLine } from '@/components/listings/listingCard/ListingCardSkeleton/ui'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useCardLocation } from '@/hooks/location/useCardLocation'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import CompareCardActions from './CompareCardActions'
import * as S from './ui'
import { useCompareColumnRunway } from './useCompareColumnRunway'
import {
  type CompareTlc,
  useCompareShippingQuotes,
} from './useCompareShippingQuotes'

const FALLBACK_IMAGE = '/home-portal/images/fallbackListingImage.png'

type Section = 'auction' | 'inventory' | 'logistics'
const SECTIONS: Section[] = ['auction', 'inventory', 'logistics']

type RowHelpers = {
  empty: string
}

type Row = {
  key: string
  section: Section
  winner?: 'lowest' | 'highest'
  display?: 'money' | 'percent'
  /** No winner marks unless every column has a value. */
  requireAll?: boolean
  /** Cells carry a per-column pending/unavailable state. */
  quoteDerived?: boolean
  value: (
    l: CombinedListingSearchResult,
    tlc?: CompareTlc
  ) => string | number | null
  render: (
    l: CombinedListingSearchResult,
    h: RowHelpers,
    tlc?: CompareTlc
  ) => ReactNode
}

const num = (v: number | null | undefined): number | null =>
  v == null || Number.isNaN(v) ? null : v

const pctOfMsrp = (l: CombinedListingSearchResult): number | null =>
  l.retailPrice == null || l.pricePercentOfMsrp <= 0
    ? null
    : l.pricePercentOfMsrp * 100

const Money = ({ value, currency }: { value: number; currency: string }) => (
  <CurrencyStringDollars value={value} currency={currency} />
)

const Percent = ({ value }: { value: number }) => (
  <FormattedNumber
    value={value / 100}
    style="percent"
    maximumFractionDigits={1}
    minimumFractionDigits={1}
  />
)

const moneyOrEmpty = (
  v: number | null,
  currency: string,
  empty: string
): ReactNode => (v == null ? empty : <Money value={v} currency={currency} />)

const ShipsFrom = ({
  listing,
  empty,
}: {
  listing: CombinedListingSearchResult
  empty: string
}) => {
  const { sellerCity, provinceCode, countryCode } = listing.originalSearchResult
  const location = useCardLocation({
    city: sellerCity,
    provinceCode,
    countryCode,
  })
  return <>{location || empty}</>
}

/** Renders pending vs unavailable differently, so a slow column reads as slow. */
const QuoteValue = ({
  tlc,
  value,
  currency,
  empty,
  rowKey,
}: {
  tlc: CompareTlc | undefined
  value: number | null
  currency: string
  empty: string
  rowKey: string
}) => {
  const [emptyCell, setEmptyCell] = useState<HTMLSpanElement | null>(null)

  // Decorative — the cell's own aria-busy is the loading signal.
  if (tlc?.status === 'pending')
    return (
      <S.QuotePending aria-hidden="true">
        <SkeletonTextLine width="4rem" testIdSuffix={`compare-${rowKey}`} />
      </S.QuotePending>
    )

  // A ready-but-empty cell is a withheld price, not a failed quote.
  if (value == null)
    return tlc?.status === 'unavailable' ? (
      <>
        <span ref={setEmptyCell}>{empty}</span>
        <Tooltip referenceElement={emptyCell}>
          <FormattedMessage id="Listings.ComparePanel.quoteUnavailable" />
        </Tooltip>
      </>
    ) : (
      <>{empty}</>
    )

  return <Money value={value} currency={currency} />
}

const hasManifest = (l: CombinedListingSearchResult): boolean =>
  l.pricingStrategy === ListingDtoPricingStrategyEnum.Auction
    ? l.listingType !== 'CONTRACT'
    : true

const ROWS: Row[] = [
  {
    key: 'listingType',
    section: 'auction',
    value: (l) =>
      getPrimaryBuyingFormat(
        getDisplayBuyingFormats({
          listing: l.originalSearchResult,
          enableFirstDibs: false,
        })
      ),
    render: (l) => <BuyingFormat listing={l.originalSearchResult} />,
  },
  {
    key: 'timeLeft',
    section: 'auction',
    value: (l) => l.endTime.getTime(),
    render: (l) => <Countdown target={l.endTime} />,
  },
  {
    key: 'bidsPlaced',
    section: 'auction',
    value: (l) => num(l.numberOfBids),
    render: (l, { empty }) => num(l.numberOfBids) ?? empty,
  },
  {
    key: 'currentBid',
    section: 'auction',
    value: (l) => (l.currentPrice > 0 ? num(l.currentPrice) : null),
    render: (l, { empty }) =>
      l.currentPrice > 0 ? (
        <Money value={l.currentPrice} currency={l.currency} />
      ) : (
        empty
      ),
  },
  {
    key: 'category',
    section: 'inventory',
    value: (l) => l.categories[0] ?? null,
    render: (l, { empty }) => l.categories[0] ?? empty,
  },
  {
    key: 'condition',
    section: 'inventory',
    value: (l) => l.displayedCondition || null,
    render: (l, { empty }) =>
      l.displayedCondition ? transformCondition(l.displayedCondition) : empty,
  },
  {
    key: 'units',
    section: 'inventory',
    winner: 'highest',
    value: (l) => (l.units ? num(l.units) : null),
    render: (l, { empty }) => l.units || empty,
  },
  {
    key: 'msrp',
    section: 'inventory',
    value: (l) => num(l.retailPrice),
    render: (l, { empty }) =>
      moneyOrEmpty(num(l.retailPrice), l.currency, empty),
  },
  {
    key: 'percentOfMsrp',
    section: 'inventory',
    winner: 'lowest',
    display: 'percent',
    value: (l) => pctOfMsrp(l),
    render: (l, { empty }) => {
      const pct = pctOfMsrp(l)
      return pct == null ? empty : <Percent value={pct} />
    },
  },
  {
    key: 'pricePerUnit',
    section: 'inventory',
    winner: 'lowest',
    display: 'money',
    value: (l) => (l.unitPrice > 0 ? num(l.unitPrice) : null),
    render: (l, { empty }) =>
      l.unitPrice > 0 ? (
        <Money value={l.unitPrice} currency={l.currency} />
      ) : (
        empty
      ),
  },
  {
    key: 'manifest',
    section: 'inventory',
    value: (l) => (hasManifest(l) ? 'yes' : null),
    render: (l, { empty }) =>
      hasManifest(l) ? (
        <S.ManifestLink
          appearance="plainTertiary"
          href={`${l.auctionUrl}#manifest-section`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage id="Listings.ListView.viewManifest" />
        </S.ManifestLink>
      ) : (
        empty
      ),
  },
  {
    key: 'shipsFrom',
    section: 'logistics',
    value: (l) => l.originalSearchResult.sellerCity || null,
    render: (l, { empty }) => <ShipsFrom listing={l} empty={empty} />,
  },
  {
    key: 'palletCount',
    section: 'logistics',
    value: (l) => num(l.originalSearchResult.palletCount),
    render: (l, { empty }) => num(l.originalSearchResult.palletCount) ?? empty,
  },
  {
    key: 'shipMethod',
    section: 'logistics',
    value: (l) => l.shipmentType || null,
    render: (l, { empty }) => l.shipmentType || empty,
  },
  {
    key: 'shippingFee',
    section: 'logistics',
    winner: 'lowest',
    display: 'money',
    requireAll: true,
    quoteDerived: true,
    value: (_l, tlc) => num(tlc?.shippingFee),
    render: (l, { empty }, tlc) => (
      <QuoteValue
        tlc={tlc}
        value={num(tlc?.shippingFee)}
        currency={l.currency}
        empty={empty}
        rowKey="shippingFee"
      />
    ),
  },
  {
    key: 'totalLanded',
    section: 'logistics',
    winner: 'lowest',
    display: 'money',
    requireAll: true,
    quoteDerived: true,
    value: (_l, tlc) => num(tlc?.totalLandedCost),
    render: (l, { empty }, tlc) => (
      <QuoteValue
        tlc={tlc}
        value={num(tlc?.totalLandedCost)}
        currency={l.currency}
        empty={empty}
        rowKey="totalLanded"
      />
    ),
  },
]

type FormatNumber = IntlShape['formatNumber']

const displayedValue = (
  row: Row,
  value: number,
  currency: string,
  formatNumber: FormatNumber
): string => {
  if (row.display === 'money')
    return formatNumber(value, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  if (row.display === 'percent')
    return formatNumber(value / 100, {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })
  return String(value)
}

const winnerDisplay = (
  row: Row,
  listings: CombinedListingSearchResult[],
  formatNumber: FormatNumber,
  tlcFor: (l: CombinedListingSearchResult) => CompareTlc | undefined,
  /** Amounts in different currencies are never converted, so they don't rank. */
  suppressMoney: boolean
): string | null => {
  if (!row.winner) return null
  if (suppressMoney && row.display === 'money') return null
  const scored = listings
    .map((l) => ({ listing: l, value: row.value(l, tlcFor(l)) }))
    .filter(
      (e): e is { listing: CombinedListingSearchResult; value: number } =>
        typeof e.value === 'number'
    )
  if (scored.length < 2) return null
  if (row.requireAll && scored.length < listings.length) return null
  const best = scored.reduce((a, b) =>
    row.winner === 'lowest'
      ? b.value < a.value
        ? b
        : a
      : b.value > a.value
        ? b
        : a
  )
  return displayedValue(row, best.value, best.listing.currency, formatNumber)
}

type ComparePanelProps = {
  banner?: ReactNode
}

export const ComparePanel = ({ banner }: ComparePanelProps) => {
  const { formatMessage, formatNumber } = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const badgeProps = useCompareBadgeProps()
  const {
    selectedListings,
    toggleListing,
    isPanelOpen: isOpen,
    closePanel: onClose,
  } = useCompareSelection()
  const { byListingId: tlcByListingId } = useCompareShippingQuotes(
    selectedListings,
    isOpen
  )
  const titleId = useId()

  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  const listingsRef = useRef(selectedListings)
  // oxlint-disable-next-line react/refs
  listingsRef.current = selectedListings

  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen])

  const isEmpty = selectedListings.length === 0

  useEffect(() => {
    if (isOpen && isEmpty) onCloseRef.current()
  }, [isOpen, isEmpty])

  const viewedRef = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      viewedRef.current = false
      return
    }
    if (viewedRef.current || isEmpty) return
    viewedRef.current = true
    trackButtonClicked({
      buttonName: 'compare_listings_modal_viewed',
      listings_bid_on: toListingsBidOn(listingsRef.current),
      ...badgeProps(),
    })
  }, [isOpen, isEmpty, trackButtonClicked, badgeProps])

  useCompareColumnRunway(
    scrollAreaRef,
    gridRef,
    selectedListings.length,
    isOpen
  )

  if (!isOpen || isEmpty || typeof document === 'undefined') return null

  const empty = formatMessage({ id: 'Listings.ComparePanel.emptyValue' })
  const columns = selectedListings.length
  const currencies = [...new Set(selectedListings.map((l) => l.currency))]
  const isMixedCurrency = currencies.length > 1

  const target =
    document.getElementById('modal-portal-wrapper') ?? document.body

  return createPortal(
    <S.Backdrop data-testid="compare-panel" onClick={onClose}>
      <S.Panel
        role="dialog"
        aria-modal
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <S.HeaderBar>
          <S.HeaderText>
            <S.Eyebrow>
              {formatMessage({ id: 'Listings.ComparePanel.eyebrow' })}
            </S.Eyebrow>
            <S.Title id={titleId}>
              {formatMessage(
                { id: 'Listings.ComparePanel.title' },
                { count: columns }
              )}
            </S.Title>
          </S.HeaderText>
          <S.CloseButton
            type="button"
            aria-label={formatMessage({ id: 'Listings.ComparePanel.close' })}
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faXmark} />
          </S.CloseButton>
        </S.HeaderBar>

        <S.ScrollArea ref={scrollAreaRef}>
          <S.BannerArea>
            {banner}
            <CompareCurrencyBanner currencies={currencies} />
          </S.BannerArea>
          <S.Grid $columns={columns} ref={gridRef}>
            <S.HeaderCornerCell />
            {selectedListings.map((l) => (
              <S.HeaderCard
                key={l.listingId}
                data-testid="compare-panel-card"
                {...(isMixedCurrency
                  ? { [COMPARE_CARD_STACKED_ATTR]: '' }
                  : null)}
              >
                <S.RemoveButton
                  type="button"
                  aria-label={formatMessage(
                    { id: 'Listings.ComparePanel.removeListing' },
                    { title: l.title }
                  )}
                  onClick={() => toggleListing(l.listingId)}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </S.RemoveButton>
                <S.CardMain>
                  <S.CardThumb>
                    <Image
                      src={l.primaryImageUrl || FALLBACK_IMAGE}
                      alt={formatMessage(
                        { id: 'Auction.image.alt' },
                        { title: l.title }
                      )}
                      width={48}
                      height={48}
                    />
                  </S.CardThumb>
                  <S.CardInfo>
                    <StorefrontLogo listing={l.originalSearchResult} />
                    <S.CardTitle maxLines={2}>{l.title}</S.CardTitle>
                  </S.CardInfo>
                </S.CardMain>
                <S.CardFooter>
                  <S.CardBid>
                    {l.currentPrice > 0 ? (
                      <Money value={l.currentPrice} currency={l.currency} />
                    ) : (
                      empty
                    )}
                    {isMixedCurrency ? (
                      <S.CurrencyChip data-testid="compare-currency-chip">
                        {l.currency}
                      </S.CurrencyChip>
                    ) : null}
                  </S.CardBid>
                  <CompareCardActions listing={l} />
                </S.CardFooter>
              </S.HeaderCard>
            ))}

            {SECTIONS.map((section) => {
              const rows = ROWS.filter((r) => r.section === section)
              return (
                <Fragment key={section}>
                  <S.SectionHeader data-section={section}>
                    <S.SectionLabel>
                      {formatMessage({
                        id: `Listings.ComparePanel.sections.${section}`,
                      })}
                    </S.SectionLabel>
                  </S.SectionHeader>
                  {rows.map((row) => {
                    const bestDisplay = winnerDisplay(
                      row,
                      selectedListings,
                      formatNumber,
                      (l) => tlcByListingId.get(l.listingId),
                      isMixedCurrency
                    )
                    return (
                      <Fragment key={row.key}>
                        <S.LabelCell>
                          {formatMessage({
                            id: `Listings.ComparePanel.rows.${row.key}`,
                          })}
                        </S.LabelCell>
                        {selectedListings.map((l) => {
                          const tlc = tlcByListingId.get(l.listingId)
                          const v = row.value(l, tlc)
                          const isWinner =
                            bestDisplay != null &&
                            typeof v === 'number' &&
                            displayedValue(row, v, l.currency, formatNumber) ===
                              bestDisplay
                          const quoteStatus = row.quoteDerived
                            ? (tlc?.status ?? 'unavailable')
                            : undefined
                          return (
                            <S.ValueCell
                              key={l.listingId}
                              $winner={isWinner}
                              $hasValue={v != null}
                              data-row={row.key}
                              data-winner={isWinner || undefined}
                              data-has-value={v != null || undefined}
                              data-quote-status={quoteStatus}
                              aria-busy={
                                quoteStatus === 'pending' ? true : undefined
                              }
                            >
                              {isWinner ? (
                                <S.WinnerCheck>
                                  <FontAwesomeIcon icon={faCircleCheck} />
                                </S.WinnerCheck>
                              ) : null}
                              {row.render(l, { empty }, tlc)}
                            </S.ValueCell>
                          )
                        })}
                      </Fragment>
                    )
                  })}
                </Fragment>
              )
            })}
          </S.Grid>
        </S.ScrollArea>
      </S.Panel>
    </S.Backdrop>,
    target
  )
}
